/**
 * AdminSpares — Specialized form with multi-image gallery upload.
 */
import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, formatINR } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FileUpload from "@/components/FileUpload";

const INITIAL = {
  title: "", part_number: "", brand: "", category: "General", compatible_with: "",
  price: 0, discount_percent: 0, stock_qty: 0,
  image: "", images: [], description: "", specs: {}, brochure_url: "", visible: true,
};

export default function AdminSpares() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/spares"); setItems(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(INITIAL); setOpen(true); };
  const startEdit = (it) => {
    setEditing(it);
    setForm({ ...INITIAL, ...it, images: it.images || [], specs: it.specs || {} });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/spares/${editing.id}`, form);
      else await api.post("/admin/spares", form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Save failed"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    await api.delete(`/admin/spares/${it.id}`);
    toast.success("Deleted");
    load();
  };

  const setField = (k, v) => setForm({ ...form, [k]: v });
  const setImageAt = (i, v) => { const next = [...(form.images || [])]; next[i] = v; setField("images", next); };
  const removeImageAt = (i) => setField("images", (form.images || []).filter((_, idx) => idx !== i));
  const addImage = () => setField("images", [...(form.images || []), ""]);

  const specsLines = Object.entries(form.specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n");

  return (
    <div data-testid="admin-spares">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="heading-xl text-3xl">SPARES</h1>
        <button onClick={startCreate} className="btn-primary text-sm" data-testid="admin-spares-new">
          <Plus className="w-4 h-4" /> New Spare
        </button>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {["Title", "P/N", "Brand", "Category", "Price", "Stock", "Visible"].map((c) => (
                <th key={c} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest">{c}</th>
              ))}
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              : items.length === 0 ? <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">No spares yet. Click "New Spare".</td></tr>
              : items.map((it) => (
                <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`row-${it.id}`}>
                  <td className="px-4 py-3">{it.title}</td>
                  <td className="px-4 py-3 font-mono text-xs">{it.part_number || "—"}</td>
                  <td className="px-4 py-3">{it.brand}</td>
                  <td className="px-4 py-3">{it.category}</td>
                  <td className="px-4 py-3">{formatINR(it.discount_percent ? it.price * (1 - it.discount_percent / 100) : it.price)}</td>
                  <td className="px-4 py-3">{it.stock_qty}</td>
                  <td className="px-4 py-3">{it.visible ? "Yes" : "No"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(it)} className="p-1.5 hover:bg-gray-100" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(it)} className="p-1.5 hover:bg-red-50 text-red-600 ml-1" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase">{editing ? "Edit" : "New"} Spare</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Input label="Title" value={form.title} onChange={(v) => setField("title", v)} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Part Number / SKU" value={form.part_number} onChange={(v) => setField("part_number", v)} />
              <Input label="Brand" value={form.brand} onChange={(v) => setField("brand", v)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Category (e.g. Filters, Hydraulics, Engine)" value={form.category} onChange={(v) => setField("category", v)} />
              <Input label="Compatible With" value={form.compatible_with} onChange={(v) => setField("compatible_with", v)} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input label="MRP / Price (₹)" type="number" value={form.price} onChange={(v) => setField("price", Number(v) || 0)} />
              <Input label="Discount %" type="number" value={form.discount_percent} onChange={(v) => setField("discount_percent", Number(v) || 0)} />
              <Input label="Stock Qty" type="number" value={form.stock_qty} onChange={(v) => setField("stock_qty", Number(v) || 0)} />
            </div>

            <FileUpload value={form.image} onChange={(v) => setField("image", v)} label="Primary Image" accept="image/*" kind="image" />

            <FileUpload value={form.brochure_url} onChange={(v) => setField("brochure_url", v)} label="Brochure / Spec Sheet (PDF)" accept=".pdf" kind="file" />

            {/* Multi-image gallery */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 flex items-center justify-between">
                <span>Additional Gallery Images</span>
                <button type="button" onClick={addImage} className="text-[#E65C00] hover:underline inline-flex items-center gap-1" data-testid="spare-add-image">
                  <Plus className="w-3 h-3" /> Add Image
                </button>
              </div>
              <div className="mt-2 space-y-3">
                {(form.images || []).length === 0 && <div className="text-xs text-gray-500 italic">No extra images yet — click "Add Image" to upload multiple shots of this spare part.</div>}
                {(form.images || []).map((url, i) => (
                  <div key={i} className="flex gap-3 items-start" data-testid={`spare-image-row-${i}`}>
                    <div className="flex-1"><FileUpload value={url} onChange={(v) => setImageAt(i, v)} accept="image/*" kind="image" /></div>
                    <button type="button" onClick={() => removeImageAt(i)} className="p-2 text-red-600 hover:bg-red-50" aria-label="Remove" data-testid={`spare-image-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Description</label>
              <textarea rows={4} className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={form.description} onChange={(e) => setField("description", e.target.value)} />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Specifications (one per line, format: Key: Value)</label>
              <textarea rows={5} className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-xs font-mono" defaultValue={specsLines}
                onBlur={(e) => {
                  const out = {};
                  e.target.value.split("\n").forEach((l) => { const idx = l.indexOf(":"); if (idx > 0) out[l.slice(0, idx).trim()] = l.slice(idx + 1).trim(); });
                  setField("specs", out);
                }}
              />
            </div>

            <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.visible} onChange={(e) => setField("visible", e.target.checked)} /><span className="text-sm font-bold">Visible on site</span></label>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="btn-outline text-sm"><X className="w-4 h-4" /> Cancel</button>
            <button onClick={save} className="btn-primary text-sm" data-testid="spare-save"><Save className="w-4 h-4" /> Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Input({ label, type = "text", value, onChange }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{label}</label>
      <input type={type} className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
